const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:3005/api/v1';

// ============== Types ==============

export interface Invoice {
  id: string;
  stripe_invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  amount_paid: number;
  amount_due: number;
  currency: string;
  status: 'paid' | 'open' | 'void' | 'draft' | 'uncollectible';
  paid_at: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf_url: string | null;
}

export interface InvoiceLineItem {
  description: string;
  amount: number;
  quantity: number;
}

export interface InvoiceDetails extends Invoice {
  line_items: InvoiceLineItem[];
}

export interface InvoicesListParams {
  limit?: number;
  offset?: number;
  status?: 'paid' | 'open' | 'void';
}

export interface InvoicesResponse {
  success: boolean;
  data: Invoice[];
  pagination?: {
    total: number;
    limit: number;
    offset: number;
  };
  error?: string;
  message?: string;
}

export interface InvoiceDetailsResponse {
  success: boolean;
  data?: InvoiceDetails;
  error?: string;
  message?: string;
}

export interface RetryPaymentResponse {
  success: boolean;
  data?: {
    invoiceId: string;
    status: string;
    amountPaid: number;
    paidAt: string;
  };
  error?: string;
  message?: string;
}

// ============== Service ==============

class InvoiceService {
  /**
   * Get all invoices for the organization
   * GET /invoices
   */
  async getInvoices(params: InvoicesListParams = {}): Promise<InvoicesResponse> {
    const queryParams = new URLSearchParams();

    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/invoices${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    return await response.json();
  }

  /**
   * Get invoice details by Stripe invoice ID
   * GET /invoices/:id
   */
  async getInvoiceDetails(invoiceId: string): Promise<InvoiceDetailsResponse> {
    const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    return await response.json();
  }

  /**
   * Retry a failed payment
   * POST /invoices/:id/retry
   */
  async retryPayment(invoiceId: string): Promise<RetryPaymentResponse> {
    const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/retry`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    return await response.json();
  }

  /**
   * Get the most recent failed/open invoice (useful for retry payment button)
   */
  async getLatestFailedInvoice(): Promise<Invoice | null> {
    const response = await this.getInvoices({ status: 'open', limit: 1 });

    if (response.success && response.data && response.data.length > 0) {
      return response.data[0];
    }

    return null;
  }
}

export const invoiceService = new InvoiceService();
